import { useState, type FormEvent } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import { useExchanges } from '@/hooks/useExchanges';
import ExchangeModal from './components/ExchangeModal';
import { updateExchange, type ExchangeRecord } from '@/services/exchanges';
import { useAuth } from '@/context/AuthContext';
import { isOperationsRole } from '@/lib/rbac';

const STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: 'Pendiente', color: 'text-amber-600', bg: 'bg-amber-50' },
  completed: { label: 'Completado', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  cancelled: { label: 'Cancelado', color: 'text-red-600', bg: 'bg-red-50' },
};

const TYPE_LABEL: Record<string, string> = {
  size_change: 'Cambio de talla',
  product_change: 'Cambio de producto',
};

const STATUSES = ['all', 'pending', 'completed', 'cancelled'];
const STATUS_LABELS: Record<string, string> = {
  all: 'Todos',
  pending: 'Pendientes',
  completed: 'Completados',
  cancelled: 'Cancelados',
};

export default function ExchangesPage() {
  const { profile } = useAuth();
  const isOperations = isOperationsRole(profile?.role ?? '');
  const [filter, setFilter] = useState('all');
  const { data: exchanges, loading, error, refetch } = useExchanges();
  const [showNew, setShowNew] = useState(false);
  const [actionError, setActionError] = useState('');

  async function handleStatusChange(id: string, status: string) {
    setActionError('');
    const { error: updateError } = await updateExchange(id, { status });
    if (updateError) setActionError(updateError);
    refetch();
  }

  const [editingExchange, setEditingExchange] = useState<ExchangeRecord | null>(null);
  const [editReason, setEditReason] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  function openEdit(e: ExchangeRecord) {
    setEditingExchange(e);
    setEditReason(e.reason ?? '');
    setEditNotes(e.notes ?? '');
    setEditError('');
  }

  async function handleEditSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (!editingExchange) return;
    setEditSaving(true);
    setEditError('');
    const { error: updateError } = await updateExchange(editingExchange.id, {
      reason: editReason,
      notes: editNotes,
    });
    setEditSaving(false);
    if (updateError) {
      setEditError(updateError);
      return;
    }
    setEditingExchange(null);
    refetch();
  }

  const filtered = exchanges.filter((e) => filter === 'all' || e.status === filter);

  const counts: Record<string, number> = {};
  STATUSES.forEach((s) => {
    counts[s] = s === 'all' ? exchanges.length : exchanges.filter((e) => e.status === s).length;
  });

  const netDifference = exchanges
    .filter((e) => e.status === 'completed')
    .reduce((sum, e) => sum + (e.priceDifference || 0), 0);

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        {actionError && (
          <div className="flex items-center gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{actionError}</span>
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Cambios</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{exchanges.length} cambios registrados</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          {!isOperations && (
            <button
              onClick={() => setShowNew(true)}
              className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-add-line mr-1.5"></i>Nuevo Cambio
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Total Cambios</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{exchanges.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Pendientes</p>
            <p className="text-xl font-bold text-amber-600 mt-1">{counts.pending || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Completados</p>
            <p className="text-xl font-bold text-emerald-600 mt-1">{counts.completed || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Diferencia neta (completados)</p>
            <p className={`text-xl font-bold mt-1 ${netDifference >= 0 ? 'text-accent-600' : 'text-red-600'}`}>
              {netDifference >= 0 ? '+' : ''}${netDifference.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                filter === s ? 'bg-accent-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {STATUS_LABELS[s]} ({counts[s] || 0})
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Cambio</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Cliente</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden xl:table-cell">Vendedor</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden md:table-cell">Venta original</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Tipo</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden lg:table-cell">Detalle</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Diferencia</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Estado</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden xl:table-cell">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const st = STATUS_MAP[e.status] || { label: e.status, color: 'text-foreground-500', bg: 'bg-background-100' };
                  const outItem = e.items.find((i) => i.direction === 'out');
                  const inItem = e.items.find((i) => i.direction === 'in');
                  const diff = e.priceDifference || 0;
                  return (
                    <tr key={e.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-5 py-3.5 text-accent-600 font-medium whitespace-nowrap">{e.exchangeNumber}</td>
                      <td className="px-5 py-3.5">
                        <div>
                          <p className="text-foreground-900 font-medium text-sm">{e.customerName || '—'}</p>
                          {e.customerCity && <p className="text-xs text-foreground-500">{e.customerCity}</p>}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 hidden xl:table-cell text-foreground-700">{e.salespersonName || '—'}</td>
                      <td className="px-5 py-3.5 hidden md:table-cell text-foreground-700">{e.saleNumber || '—'}</td>
                      <td className="px-5 py-3.5">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">
                          {TYPE_LABEL[e.exchangeType] || e.exchangeType}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 hidden lg:table-cell">
                        <p className="text-xs text-foreground-700 max-w-[220px] truncate">
                          {outItem ? `${outItem.productName} ${outItem.sizeLabel}`.trim() : '—'} → {inItem ? `${inItem.productName} ${inItem.sizeLabel}`.trim() : '—'}
                        </p>
                      </td>
                      <td className="px-5 py-3.5 text-right whitespace-nowrap">
                        <span className={`font-medium ${diff > 0 ? 'text-amber-600' : diff < 0 ? 'text-emerald-600' : 'text-foreground-400'}`}>
                          {diff > 0 ? '+' : diff < 0 ? '−' : ''}${Math.abs(diff).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <select
                            value={e.status}
                            onChange={(ev) => handleStatusChange(e.id, ev.target.value)}
                            className={`text-xs px-2 py-1 rounded-full font-medium border-0 cursor-pointer ${st.color} ${st.bg}`}
                          >
                            {Object.keys(STATUS_MAP).map((key) => (
                              <option key={key} value={key}>{STATUS_MAP[key].label}</option>
                            ))}
                          </select>
                          {!isOperations && (
                            <button
                              onClick={() => openEdit(e)}
                              className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 hover:text-accent-600 transition-colors cursor-pointer"
                              aria-label="Editar detalles"
                            >
                              <i className="ri-edit-line text-sm"></i>
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 hidden xl:table-cell text-xs text-foreground-500 whitespace-nowrap">
                        {e.exchangeDate ? new Date(e.exchangeDate).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-inbox-line text-3xl block mb-2"></i>
              <p className="text-sm">No se encontraron cambios</p>
            </div>
          )}
          {loading && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-2xl block mb-2"></i>
              <p className="text-sm">Cargando cambios...</p>
            </div>
          )}
        </div>
      </div>

      {editingExchange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => !editSaving && setEditingExchange(null)}></div>
          <div className="relative w-full max-w-md bg-white rounded-xl border border-background-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-foreground-950">Editar detalle del cambio</h2>
                <p className="text-sm text-foreground-500 mt-0.5">{editingExchange.exchangeNumber}</p>
              </div>
              <button onClick={() => !editSaving && setEditingExchange(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 transition-colors cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Motivo</label>
                <textarea rows={2} value={editReason} onChange={(e) => setEditReason(e.target.value)} maxLength={500} className="w-full text-sm border border-background-200 rounded-lg px-3 py-2.5 bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300 resize-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Notas / detalles</label>
                <textarea rows={3} value={editNotes} onChange={(e) => setEditNotes(e.target.value)} maxLength={500} className="w-full text-sm border border-background-200 rounded-lg px-3 py-2.5 bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300 resize-none" placeholder="Detalles adicionales del cambio..." />
              </div>

              {editError && (
                <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
                  <i className="ri-error-warning-line"></i>
                  <span>{editError}</span>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => !editSaving && setEditingExchange(null)} className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                  Cancelar
                </button>
                <button type="submit" disabled={editSaving} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
                  {editSaving ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showNew && <ExchangeModal onClose={() => setShowNew(false)} onSaved={refetch} />}
    </AdminLayout>
  );
}